import React, { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import CustomInput from "../../../CustomComponent/custom_input";
import CustomTextArea from "../../../CustomComponent/custom_textarea";
import FormSubmitButton from "../../../Components/layouts/formSubmitButton";
import Footer from "../../../Components/Footer/footer";
import StepRoutes from "../../../Routes/steps_routes";
import { FcCamcorderPro, FcCollaboration } from "react-icons/fc";
import performRequest from "../../../api/request";
import { Toast } from "../../../CustomComponent/toast";
import axios from "axios";
import { MediaTaskAssign } from "../../../SchemaInfo/info";
import { MediaAssignTaskSchema } from "../../../Schemas/Media And Marketing/mediaDelivery.schema";
import CustomSelect from "../../../CustomComponent/custom_select";
import SimpleButton from "../../../Components/layouts/simple_button";
import { useNavigate } from "react-router-dom";

const CreateMarketingDeliveryTask = () => {
  const [agencies, setAgencies] = useState([]);
  const [loadingAgencies, setLoadingAgencies] = useState(false);
  const navigate = useNavigate();

  // =========================================================
  // GET AGENCY LIST
  // =========================================================

  const getAgencyList = async () => {
    try {
      setLoadingAgencies(true);

      const response = await performRequest(
        "GET",
        "/mediaDelivery/get-agencies"
      );

      if (response?.status === 200 || response?.status === 201) {
        setAgencies(response?.data?.data || []);
      }
    } catch (error) {
      console.error("Get agency error:", error);

      Toast("error", "Failed to fetch agency list");
    } finally {
      setLoadingAgencies(false);
    }
  };

  useEffect(() => {
    getAgencyList();
  }, []);

  // =========================================================
  // IMAGE CHANGE
  // =========================================================

  const handleImageChange = (event, values, setFieldValue) => {
    const files = Array.from(event.target.files || []);

    if (!files.length) {
      return;
    }

    const updatedFiles = [...(values.rawImages || []), ...files];

    setFieldValue("rawImages", updatedFiles);

    // Allow selecting same file again
    event.target.value = "";
  };

  // =========================================================
  // VIDEO CHANGE
  // =========================================================

  const handleVideoChange = (event, values, setFieldValue) => {
    const files = Array.from(event.target.files || []);

    if (!files.length) {
      return;
    }

    const updatedFiles = [...(values.rawVideos || []), ...files];

    setFieldValue("rawVideos", updatedFiles);

    // Allow selecting same file again
    event.target.value = "";
  };

  // =========================================================
  // REMOVE IMAGE
  // =========================================================

  const removeImage = (index, values, setFieldValue) => {
    const updatedFiles = values.rawImages.filter(
      (_, fileIndex) => fileIndex !== index
    );

    setFieldValue("rawImages", updatedFiles);
  };

  // =========================================================
  // REMOVE VIDEO
  // =========================================================

  const removeVideo = (index, values, setFieldValue) => {
    const updatedFiles = values.rawVideos.filter(
      (_, fileIndex) => fileIndex !== index
    );

    setFieldValue("rawVideos", updatedFiles);
  };

  // =========================================================
  // ADD LINK
  // =========================================================

  const addLink = (values, setFieldValue) => {
    setFieldValue("links", [
      ...(values.links || []),
      {
        title: "",
        url: "",
      },
    ]);
  };

  // =========================================================
  // REMOVE LINK
  // =========================================================

  const removeLink = (index, values, setFieldValue) => {
    const updatedLinks = values.links.filter(
      (_, linkIndex) => linkIndex !== index
    );

    setFieldValue("links", updatedLinks);
  };

  // =========================================================
  // SUBMIT
  // =========================================================

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      setSubmitting(true);

      let uploadedImages = [];
      let uploadedVideos = [];

      // ================================
      // UPLOAD IMAGES TO GCP
      // ================================

      for (const file of values.rawImages || []) {
        const signedRes = await performRequest(
          "POST",
          "/postPlanning/generateUploadUrl",
          {
            fileName: file.name,
            contentType: file.type,
            fileType: "image",
          }
        );

        await axios.put(signedRes.data.uploadUrl, file, {
          headers: {
            "Content-Type": file.type,
          },
        });

        uploadedImages.push({
          fileName: file.name,

          fileType: file.type,

          url: signedRes.data.publicUrl,
        });
      }

      // ================================
      // UPLOAD VIDEOS TO GCP
      // ================================

      for (const file of values.rawVideos || []) {
        const signedRes = await performRequest(
          "POST",
          "/postPlanning/generateUploadUrl",
          {
            fileName: file.name,
            contentType: file.type,
            fileType: "video",
          }
        );

        await axios.put(signedRes.data.uploadUrl, file, {
          headers: {
            "Content-Type": file.type,
          },
        });

        uploadedVideos.push({
          fileName: file.name,

          fileType: file.type,

          url: signedRes.data.publicUrl,
        });
      }

      // ================================
      // GET SELECTED AGENCY
      // ================================

      const selectedAgency = agencies.find(
        (agency) => String(agency.id) === String(values.agencyId)
      );

      // Remove empty links

      const filteredLinks = (values.links || []).filter(
        (link) => link.title?.trim() || link.url?.trim()
      );

      // ================================
      // FINAL PAYLOAD
      // ================================

      const payload = {
        taskName: values.taskName,
        brandAsset: values.brandAsset,
        taskDescription: values.taskDescription,

        agencyId: Number(values.agencyId),

        agencyName: selectedAgency?.userName || "",

        rawImages: uploadedImages,

        rawVideos: uploadedVideos,

        links: filteredLinks,

        instructions: values.instructions,

        deadline: values.deadline,
      };

      const response = await performRequest(
        "POST",
        "/mediaDelivery/create-task",
        payload
      );

      if (response?.status === 200 || response?.status === 201) {
        Toast("success", "Task assigned successfully");
        navigate("/get-all-media-task");
        resetForm();
      }
    } catch (error) {
      Toast("error", "Failed to create task");
    } finally {
      setSubmitting(false);
    }
  };

  // =========================================================
  // UI
  // =========================================================

  return (
    <div className="container-fluid">
      <div className="page">
        <div className="page-wrapper">
          <div className="container-xl">
            <div className="page-header d-print-none">
              <div className="row g-2 align-items-center">
                <div className="col">
                  <h2 className="page-title mb-1">
                    <FcCamcorderPro></FcCamcorderPro> Marketing Delivery
                  </h2>
                  <h4 className="text-muted">
                    <StepRoutes />
                  </h4>
                </div>
              </div>
            </div>
            <div className="card">
              {/* =================================================
            HEADER
        ================================================= */}

              <div className="card-header">
                <h3 className="card-title">Create & Assign Marketing Task</h3>
              </div>

              {/* =================================================
            FORMIK
        ================================================= */}

              <Formik
                initialValues={MediaTaskAssign}
                validationSchema={MediaAssignTaskSchema}
                onSubmit={handleSubmit}
              >
                {({ values, errors, touched, setFieldValue, isSubmitting }) => (
                  <Form>
                    <div className="card-body">
                      {/* =================================================
                    TASK DETAILS
                ================================================= */}

                      <div className="mb-3 row">
                        <label className="w-25 form-label col-3">
                          Task Name
                        </label>

                        <div className="w-100 col">
                          <CustomInput
                            name="taskName"
                            placeholder="Enter task name"
                            required
                          />
                          {touched.taskName && errors.taskName && (
                            <div className="text-danger mt-1">
                              {errors.taskName}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="mb-3 row">
                        <label className="w-25 form-label col-3">
                          Branding Assets
                        </label>
                        <div className="w-100 col">
                          <CustomSelect
                            name="brandAsset"
                            className="form-control"
                          >
                            <option value="">Select</option>
                            <option value="Kryzen Biotech">
                              Kryzen Biotech{" "}
                            </option>
                            <option value="Hydroponic Masterclass">
                              Hydroponic Masterclass
                            </option>
                            <option value="Sudhir Devkar">Sudhir Devkar</option>
                            <option value="Eat Pure Greens">
                              Eat Pure Greens
                            </option>
                          </CustomSelect>

                          {touched.podcastBrand && errors.podcastBrand && (
                            <p className="text-danger">
                              {" "}
                              {errors.podcastBrand}{" "}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="row mb-3">
                        <label className="form-label col-3">
                          Assign Agency
                        </label>

                        <div className="col-9">
                          <select
                            className="form-select"
                            name="agencyId"
                            value={values.agencyId || ""}
                            onChange={(e) => {
                              const agencyId = e.target.value;

                              const selectedAgency = agencies.find(
                                (agency) =>
                                  String(agency.id) === String(agencyId)
                              );

                              setFieldValue("agencyId", agencyId);

                              setFieldValue(
                                "agencyName",
                                selectedAgency?.userName || ""
                              );
                            }}
                          >
                            <option value="">Select Agency</option>

                            {agencies?.map((agency) => (
                              <option key={agency.id} value={agency.id}>
                                {agency.userName}
                              </option>
                            ))}
                          </select>

                          {touched.agencyId && errors.agencyId && (
                            <div className="text-danger mt-1">
                              {errors.agencyId}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* =================================================
                    TASK DESCRIPTION
                ================================================= */}

                      <div className="mb-3 row">
                        <label className="w-25 form-label col-3">
                          Task Description
                        </label>
                        <div className="w-100 col">
                          <CustomTextArea
                            label="Task Description"
                            name="taskDescription"
                            placeholder="Enter task description"
                            rows={4}
                            required
                          />

                          {touched.taskDescription &&
                            errors.taskDescription && (
                              <div className="text-danger mt-1">
                                {errors.taskDescription}
                              </div>
                            )}
                        </div>
                      </div>

                      {/* =================================================
                    DEADLINE
                ================================================= */}

                      <div className="mb-3 row">
                        <label className="w-25 form-label col-3">
                          Deadline
                        </label>
                        <div className="w-100 col">
                          <CustomInput
                            label="Deadline"
                            name="deadline"
                            type="date"
                            required
                          />

                          {touched.deadline && errors.deadline && (
                            <div className="text-danger mt-1">
                              {errors.deadline}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* =================================================
                    RAW IMAGES
                ================================================= */}

                      <div className="mb-4 row">
                        <label className="w-25 form-label col-3">
                          Raw Images
                        </label>
                        <div className="w-100 col">
                          <input
                            type="file"
                            className="form-control"
                            multiple
                            accept="image/*"
                            onChange={(event) =>
                              handleImageChange(event, values, setFieldValue)
                            }
                          />

                          {values.rawImages?.length > 0 && (
                            <div className="mt-3">
                              <div className="fw-bold mb-2">
                                Selected Images ({values.rawImages.length})
                              </div>

                              {values.rawImages?.map((file, index) => {
                                const imageUrl =
                                  file instanceof File
                                    ? URL.createObjectURL(file)
                                    : file?.url;

                                return (
                                  <div
                                    key={index}
                                    className="d-inline-block position-relative me-3 mb-3"
                                  >
                                    <img
                                      src={imageUrl}
                                      alt="preview"
                                      style={{
                                        width: "120px",
                                        height: "120px",
                                        objectFit: "cover",
                                        borderRadius: "8px",
                                        border: "1px solid #ddd",
                                      }}
                                    />

                                    <button
                                      type="button"
                                      className="btn btn-sm btn-danger position-absolute"
                                      style={{
                                        top: "5px",
                                        right: "5px",
                                      }}
                                      onClick={() =>
                                        removeImage(
                                          index,
                                          values,
                                          setFieldValue
                                        )
                                      }
                                    >
                                      ×
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* =================================================
                    RAW VIDEOS
                ================================================= */}

                      <div className="mb-4 row">
                        <label className="w-25 form-label col-3">
                          Raw Videos
                        </label>
                        <div className="w-100 col">
                          <input
                            type="file"
                            className="form-control"
                            multiple
                            accept="video/*"
                            onChange={(event) =>
                              handleVideoChange(event, values, setFieldValue)
                            }
                          />

                          {values.rawVideos?.map((file, index) => {
                            const videoUrl =
                              file instanceof File
                                ? URL.createObjectURL(file)
                                : file?.url;

                            return (
                              <div
                                key={index}
                                className="d-inline-block position-relative me-3 mb-3 mt-3"
                              >
                                <video
                                  src={videoUrl}
                                  controls
                                  style={{
                                    width: "180px",
                                    height: "120px",
                                    objectFit: "cover",
                                    borderRadius: "8px",
                                    border: "1px solid #ddd",
                                  }}
                                />

                                <button
                                  type="button"
                                  className="btn btn-sm btn-danger position-absolute"
                                  style={{
                                    top: "5px",
                                    right: "5px",
                                  }}
                                  onClick={() =>
                                    removeVideo(index, values, setFieldValue)
                                  }
                                >
                                  ×
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      <div className="mb-3 row">
                        <label className="w-25 form-label col-3">
                          Instruction
                        </label>
                        <div className="w-100 col">
                          <CustomTextArea
                            label="Additional Instructions"
                            name="instructions"
                            placeholder="Enter additional instructions for the agency"
                            rows={5}
                          />
                        </div>
                      </div>

                      {/* =================================================
                    REFERENCE LINKS
                ================================================= */}

                      <div className="mb-4 row">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <label className="form-label fw-bold mb-0">
                            Reference Links
                          </label>

                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={() => addLink(values, setFieldValue)}
                          >
                            + Add Link
                          </button>
                        </div>

                        {values.links.map((link, index) => (
                          <div className="border rounded p-3 mb-3" key={index}>
                            <div className="row">
                              {/* LINK TITLE */}

                              <div className="col-md-5">
                                <CustomInput
                                  label="Link Title"
                                  name={`links[${index}].title`}
                                  placeholder="Enter link title"
                                />
                              </div>

                              {/* LINK URL */}

                              <div className="col-md-5">
                                <CustomInput
                                  label="URL"
                                  name={`links[${index}].url`}
                                  placeholder="https://..."
                                />
                              </div>

                              {/* REMOVE */}

                              <div className="col-md-2 d-flex align-items-end mb-3">
                                {values.links.length > 1 && (
                                  <button
                                    type="button"
                                    className="btn btn-danger w-100"
                                    onClick={() =>
                                      removeLink(index, values, setFieldValue)
                                    }
                                  >
                                    Remove
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* =================================================
                    SELECTED AGENCY SUMMARY
                ================================================= */}

                      {values.agencyId && (
                        <div className="alert alert-info mt-4">
                          <strong>Task will be assigned to:</strong>

                          <div className="mt-1">{values.agencyName}</div>
                        </div>
                      )}
                    </div>

                    {/* =================================================
                  FOOTER
              ================================================= */}

                    <div className="card-footer d-flex justify-content-end gap-2">
                      <SimpleButton buttonText="Cancel" type="secondary" />
                      <FormSubmitButton
                        buttonText="submit"
                        loading={isSubmitting}
                        disabled={isSubmitting}
                      />
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
          <Footer />
        </div>
      </div>
    </div>
  );
};

export default CreateMarketingDeliveryTask;
